import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { apiConnector } from '@/api/api.connector';
import { DictTypes } from '@/shared/constants';
import {
  DEPENDED_DICTS_LIST,
  PRELOAD_DICT_TYPES,
  SPECIFIC_FETCH_DICT_TYPES,
  dictContentMapper,
  mapContentItemsToGetByValue
} from '@/shared/services/helpers/dicts';
import type { GetDictContentType, TDict } from '@/shared/types/dicts.types';

export const getOptimizedDictionaries = createAsyncThunk(
  'dict/getOptimizedDictionaries',
  async (
    params?: Partial<{
      dictTypes: DictTypes[];
    }>
  ): Promise<TDict> => {
    const { dictTypes: requestedDictTypes = PRELOAD_DICT_TYPES } = params || {};

    const dictContentMap = new Map<
      DictTypes,
      GetDictContentType<DictTypes>[]
    >();

    const computedSpecificDictTypes: DictTypes[] = [];
    const computedBulkDictTypes: DictTypes[] = [];

    requestedDictTypes.forEach((requestItem) => {
      // TODO Добавить справочники, которые будут тянуться отдельно от остальных
      // if (SPECIFIC_FETCH_DICT_TYPES.some((item) => item === requestItem)) {
      //   computedSpecificDictTypes.push(requestItem);
      // } else {
      //   computedBulkDictTypes.push(requestItem);
      // }
      computedBulkDictTypes.push(requestItem);
    });

    // Добавляем зависимые справочники от mainDictType, если их нет
    DEPENDED_DICTS_LIST.forEach(([mainDictType, dependDictTypes]) => {
      if (requestedDictTypes.includes(mainDictType)) {
        dependDictTypes.forEach((dependDictType) => {
          if (
            SPECIFIC_FETCH_DICT_TYPES.some((item) => item === dependDictType)
          ) {
            if (!computedSpecificDictTypes.includes(dependDictType)) {
              computedSpecificDictTypes.push(dependDictType);
            }
          } else {
            if (!computedBulkDictTypes.includes(dependDictType)) {
              computedBulkDictTypes.push(dependDictType);
            }
          }
        });
      }
    });

    // Получаем справочники через bulk
    if (computedBulkDictTypes.length > 0) {
      const responseDicts = await apiConnector.getOptimizedDictionaries(
        computedBulkDictTypes
      );
      responseDicts.forEach((responseDict) => {
        dictContentMap.set(responseDict.name, responseDict.content);
      });
    }

    // Получаем специфичные справочники
    for (const item of SPECIFIC_FETCH_DICT_TYPES) {
      if (!computedSpecificDictTypes.includes(item)) continue;

      const fetchFunction = {
        [DictTypes.Cities]: async () => {
          const content = await apiConnector.getCitiesForFilter('all');
          dictContentMap.set(item, content);
        }
      } satisfies Record<
        (typeof SPECIFIC_FETCH_DICT_TYPES)[number],
        () => Promise<void>
      >;

      if (fetchFunction[item]) {
        try {
          await fetchFunction[item]();
        } catch (error) {
          console.error(`Error fetching ${item}:`, error);
        }
      }
    }

    const initialDict: Partial<TDict> = {};
    const allFetchDictTypes: DictTypes[] = [
      ...computedBulkDictTypes,
      ...computedSpecificDictTypes
    ];

    const iterateDictTypes = <T extends DictTypes>(dictType: T) => {
      // Заполняем справочник
      dictContentMapper(dictType, dictContentMap, initialDict);
      // Заполняем elements
      if (initialDict[dictType]) {
        initialDict[dictType].elements = mapContentItemsToGetByValue(
          initialDict[dictType]?.content ?? []
        );
      }
    };
    allFetchDictTypes.forEach(iterateDictTypes);
    const dicts = initialDict as TDict;

    return dicts;
  }
);

interface IDictStore {
  dicts: TDict | null;
  dictStatus: 'idle' | 'pending' | 'fulfilled' | 'rejected';
  isLoading: boolean;
}

const initialState: IDictStore = {
  dicts: null,
  dictStatus: 'idle',
  isLoading: false
};

const dictSlice = createSlice({
  name: 'dict',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOptimizedDictionaries.pending, (state) => {
        state.dictStatus = 'pending';
        state.isLoading = true;
      })
      .addCase(getOptimizedDictionaries.rejected, (state) => {
        state.dictStatus = 'rejected';
        state.isLoading = false;
      })
      .addCase(getOptimizedDictionaries.fulfilled, (state, action) => {
        state.dictStatus = 'fulfilled';
        state.isLoading = false;
        state.dicts = { ...(state.dicts ?? {}), ...action.payload };
      });
  }
});

export default dictSlice;
